import { css } from 'styled-components';
import { checkPixel } from 'utils';
import { theme } from './theme';

const apply = (prop, value) => (value ? `${prop}: ${value};` : '');

// common
export const common = css`
  ${({ $display, $overflow, $overflowY, $visibility, $transform, $cursor, $transition, $shadow, $bgColor }) => css`
    ${apply('display', $display)}
    ${apply('overflow', $overflow)}
    ${apply('overflow-y', $overflowY)}
    ${apply('visibility', $visibility)}
    ${apply('transform', $transform)}
    ${apply('cursor', $cursor)}
    ${apply('transition', $transition)}
    ${apply('box-shadow', theme.shadows[$shadow])}
    ${apply('background-color', theme.colors[$bgColor] || $bgColor)}
    
     &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 4px;
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.scroll};
      border-radius: 10px;
    }
  `}
`;

// flex
export const flex = css`
  ${({ $flex, $flexWrap, $flexDirection, $justifyContent, $alignItems, $order, $gap, $rowGap, $columnGap }) => css`
    ${apply('flex', $flex)}
    ${apply('flex-wrap', $flexWrap)}
    ${apply('flex-direction', $flexDirection)}
    ${apply('justify-content', $justifyContent)}
    ${apply('align-items', $alignItems)}
    ${apply('order', $order)}
    ${apply('gap', checkPixel($gap))}
    ${apply('row-gap', checkPixel($rowGap))}
    ${apply('column-gap', checkPixel($columnGap))}
  `}
`;

// space
export const space = css`
  ${({
    $padding,
    $paddingTop,
    $paddingRight,
    $paddingBottom,
    $paddingLeft,
    $margin,
    $marginTop,
    $marginRight,
    $marginBottom,
    $marginLeft,
    $position,
    $top,
    $right,
    $bottom,
    $left,
    $zIndex,
    $width,
    $maxWidth,
    $minWidth,
    $height,
    $maxHeight,
    $minHeight,
  }) => css`
    ${apply('padding', $padding)}
    ${apply('padding-top', checkPixel($paddingTop))}
    ${apply('padding-right', checkPixel($paddingRight))}
    ${apply('padding-bottom', checkPixel($paddingBottom))}
    ${apply('padding-left', checkPixel($paddingLeft))}
    ${apply('margin', $margin)}
    ${apply('margin-top', checkPixel($marginTop))}
    ${apply('margin-right', checkPixel($marginRight))}
    ${apply('margin-bottom', checkPixel($marginBottom))}
    ${apply('margin-left', checkPixel($marginLeft))}
    ${apply('position', $position)}
    ${apply('top', checkPixel($top))}
    ${apply('right', checkPixel($right))}
    ${apply('bottom', checkPixel($bottom))}
    ${apply('left', checkPixel($left))}
    ${apply('z-index', $zIndex)}
    ${apply('width', checkPixel($width))}
    ${apply('max-width', checkPixel($maxWidth))}
    ${apply('min-width', checkPixel($minWidth))}
    ${apply('height', checkPixel($height))}
    ${apply('max-height', checkPixel($maxHeight))}
    ${apply('min-height', checkPixel($minHeight))}
  `}
`;

// typography
const getType = ($type) => {
  const fontType = { r: 400, m: 500, b: 700 };
  const match = $type.match(/^([a-z]*)(\d+)?$/i) || [];
  const weight = fontType[match[1]] || 400;
  const size = match[2] || 16;
  let lineHeight = size >= 38 ? 1.2 : 1.5;

  if ($type.startsWith('l')) {
    lineHeight = 1;
  }

  return `font-weight: ${weight}; font-size: ${size}px; line-height: ${lineHeight};`;
};

export const typography = css`
  ${({ $type, $color, $wordBreak, $whiteSpace, $textAlign, $maxLine, $fontWeight, $lineHeight }) => css`
    ${getType($type || 'r')}
    ${apply('color', theme.colors[$color] || $color || theme.colors.normal)}
    ${apply('word-break', $wordBreak)}
    ${apply('white-space', $whiteSpace)}
    ${apply('text-align', $textAlign)}
    ${$maxLine &&
    `overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: ${$maxLine};`}
    ${apply('font-weight', $fontWeight)}
    ${apply('line-height', $lineHeight)}
  `}
`;

// border
const borderStyle = ({ width = 1, style = 'solid', color = 'gray200' }, theme) =>
  `${checkPixel(width)} ${style} ${theme.colors[color]}`;

export const border = css`
  ${({ $borderRadius, $border, $borderTop, $borderRight, $borderBottom, $borderLeft, theme }) => css`
    ${$borderRadius && apply('border-radius', checkPixel($borderRadius))}
    ${$border && apply('border', borderStyle($border, theme))}
    ${$borderTop && apply('border-top', borderStyle($borderTop, theme))}
    ${$borderRight && apply('border-right', borderStyle($borderRight, theme))}
    ${$borderBottom && apply('border-bottom', borderStyle($borderBottom, theme))}
    ${$borderLeft && apply('border-left', borderStyle($borderLeft, theme))}
  `}
`;
